/**
 * VDOT Pace Calculator
 * Based on Jack Daniels' Running Formula
 *
 * Given a race performance (distance + time), calculates VDOT (VO2max equivalent)
 * and derives training paces for all zones.
 */

// Event distances in meters
export const EVENTS = {
  '400m':          { label: '400m',          meters: 400 },
  '600m':          { label: '600m',          meters: 600 },
  '800m':          { label: '800m',          meters: 800 },
  '1000m':         { label: '1000m',         meters: 1000 },
  '1200m':         { label: '1200m',         meters: 1200 },
  '1500m':         { label: '1500m',         meters: 1500 },
  '1600m':         { label: '1600m',         meters: 1600 },
  'mile':          { label: 'Mile',          meters: 1609.34 },
  '3000m':         { label: '3000m',         meters: 3000 },
  '3200m':         { label: '3200m',         meters: 3200 },
  '2-mile':        { label: '2 Mile',        meters: 3218.68 },
  '5000m':         { label: '5000m (Track)', meters: 5000 },
  '10000m':        { label: '10,000m',       meters: 10000 },
  '3mi-xc':        { label: '3 Mile (XC)',   meters: 4828.03 },
  '5K-xc':         { label: '5K (XC)',       meters: 5000 },
  '6K-xc':         { label: '6K (XC)',       meters: 6000 },
  '8K-xc':         { label: '8K (XC)',       meters: 8000 },
  '10K-xc':        { label: '10K (XC)',      meters: 10000 },
  '5K-road':       { label: '5K (Road)',     meters: 5000 },
  '10K-road':      { label: '10K (Road)',    meters: 10000 },
  'half-marathon': { label: 'Half Marathon', meters: 21097.5 },
  'marathon':      { label: 'Marathon',      meters: 42195 },

  // ── Relay RACE events ───────────────────────────────────────────
  // These keys go in `races.event`. The athlete's actual running
  // distance on race day is tracked separately via `races.relay_leg`;
  // this key's `meters` is the total combined distance across all
  // legs (used only for display / validation, not for VDOT — we VDOT
  // off the split, not the relay total).
  '4x100m-relay':  { label: '4×100m Relay',  meters: 400 },
  '4x200m-relay':  { label: '4×200m Relay',  meters: 800 },
  '4x400m-relay':  { label: '4×400m Relay',  meters: 1600 },
  '4x800m-relay':  { label: '4×800m Relay',  meters: 3200 },
  '4x1600m-relay': { label: '4×1600m Relay', meters: 6400 },
  'smr-800':       { label: '800 SMR (100-100-200-400)', meters: 800 },
  'smr-1600':      { label: '1600 SMR (200-200-400-800)', meters: 1600 },
  'dmr':           { label: 'DMR (1200-400-800-1600)',    meters: 4000 },

  // ── Relay SPLIT pseudo-events ───────────────────────────────────
  // These keys go in `benchmarks.event` for improvement tracking.
  // Distinct namespace from the open-track '400m'/'800m'/etc. so a
  // 2:05 in a relay 800m leg (flying start) is never compared
  // against a 2:05 open 800m (standing start).
  '100m-split':    { label: '100m (split)',  meters: 100 },
  '200m-split':    { label: '200m (split)',  meters: 200 },
  '400m-split':    { label: '400m (split)',  meters: 400 },
  '800m-split':    { label: '800m (split)',  meters: 800 },
  '1200m-split':   { label: '1200m (split)', meters: 1200 },
  '1600m-split':   { label: '1600m (split)', meters: 1600 },

  'other':         { label: 'Other',         meters: null },
};

// Training zone definitions as [minFraction, maxFraction] of VDOT velocity
const ZONES = {
  recovery:           { label: 'Recovery',          min: 0.59, max: 0.65 },
  easy:               { label: 'Easy',              min: 0.65, max: 0.74 },
  moderate:           { label: 'Moderate',          min: 0.74, max: 0.82 },
  sub_tempo:          { label: 'Sub-Tempo',         min: 0.82, max: 0.86 },
  tempo:              { label: 'Tempo',             min: 0.84, max: 0.88 },
  lt2:                { label: 'LT2',               min: 0.86, max: 0.90 },
  lt1:                { label: 'LT1',               min: 0.90, max: 0.96 },
  aerobic_power:      { label: 'Aerobic Power',     min: 0.96, max: 1.00 },
  vo2max:             { label: 'VO2 Max',           min: 1.00, max: 1.04 },
  mixed_zone:         { label: 'Mixed Zone',        min: 1.04, max: 1.10 },
  anaerobic_capacity: { label: 'Anaerobic Capacity',min: 1.10, max: 1.18 },
  anaerobic_power:    { label: 'Anaerobic Power',   min: 1.18, max: 1.28 },
  speed_endurance:    { label: 'Speed Endurance',   min: 1.28, max: 1.42 },
  speed:              { label: 'Speed',             min: 1.42, max: 1.58 },
};

/**
 * Calculate VO2 at a given velocity (meters/min)
 */
function vo2AtVelocity(v) {
  return -4.60 + 0.182258 * v + 0.000104 * v * v;
}

/**
 * Calculate % of VO2max being used at a given duration (minutes)
 */
function percentVO2maxAtTime(t) {
  return 0.8 + 0.1894393 * Math.exp(-0.012778 * t) + 0.2989558 * Math.exp(-0.1932605 * t);
}

/**
 * Calculate VDOT from a race performance
 * @param {number} distanceMeters
 * @param {number} timeSeconds
 * @returns {number} VDOT value
 */
export function calculateVDOT(distanceMeters, timeSeconds) {
  const timeMinutes = timeSeconds / 60;
  const velocity = distanceMeters / timeMinutes; // meters/min
  const vo2 = vo2AtVelocity(velocity);
  const pct = percentVO2maxAtTime(timeMinutes);
  return vo2 / pct;
}

/**
 * Given a VDOT and a fraction of it, find the corresponding velocity (meters/min)
 * Inverts the VO2 = -4.60 + 0.182258v + 0.000104v^2 quadratic
 */
function velocityFromVO2(targetVO2) {
  // 0.000104v^2 + 0.182258v + (-4.60 - targetVO2) = 0
  const a = 0.000104;
  const b = 0.182258;
  const c = -4.60 - targetVO2;
  return (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a);
}

/**
 * Format seconds into MM:SS pace string
 */
function formatPace(secondsPerUnit) {
  let mins = Math.floor(secondsPerUnit / 60);
  let secs = Math.round(secondsPerUnit % 60);
  if (secs === 60) { secs = 0; mins += 1; }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Calculate all training paces from a VDOT value
 * @param {number} vdot
 * @returns {object} zones with perMile and perKm pace strings, and velocity ranges
 */
export function pacesFromVDOT(vdot) {
  const METERS_PER_MILE = 1609.34;
  const METERS_PER_KM = 1000;

  // For elite athletes (VDOT > 65), taper aerobic zone fractions so that paces
  // don't compress into unrealistically fast territory. Developing athletes (VDOT ≤ 65)
  // are unaffected. Lower zones taper more aggressively than threshold zones.
  //   recovery/easy:               up to ~15% reduction at VDOT 78+
  //   moderate:                    up to ~7%  reduction (lighter — sits closer to threshold)
  //   sub_tempo/tempo:             up to ~6%  reduction at VDOT 78+
  //   lt2/lt1/aerobic_power/vo2max: up to ~4%  reduction at VDOT 86+ (kicks in above VDOT 73)
  const taperHigh  = vdot > 65 ? Math.max(0.85, 1 - (vdot - 65) * 0.011) : 1;
  const taperMed   = vdot > 65 ? Math.max(0.93, 1 - (vdot - 65) * 0.005) : 1;
  const taperLow   = vdot > 65 ? Math.max(0.91, 1 - (vdot - 65) * 0.006) : 1;
  // Above VDOT 73, apply a very light taper to LT2–VO2max zones. Individual variation
  // in %VO2max at threshold (85–92%) means the model can run slightly fast for some elites.
  const taperMicro = vdot > 73 ? Math.max(0.96, 1 - (vdot - 73) * 0.003) : 1;

  const result = {};

  for (const [key, zone] of Object.entries(ZONES)) {
    const taper = ['recovery', 'easy'].includes(key)                          ? taperHigh
                : key === 'moderate'                                           ? taperMed
                : ['sub_tempo', 'tempo'].includes(key)                        ? taperLow
                : ['lt2', 'lt1', 'aerobic_power', 'vo2max'].includes(key)    ? taperMicro
                : 1;

    // Use midpoint of zone for display pace
    const midFraction = (zone.min + zone.max) / 2 * taper;
    const targetVO2 = vdot * midFraction;
    const velocity = velocityFromVO2(targetVO2); // meters/min

    const secsPerMeter = 60 / velocity;
    const secsPerMile = secsPerMeter * METERS_PER_MILE;
    const secsPerKm = secsPerMeter * METERS_PER_KM;

    // Also compute range (min pace = faster velocity = higher fraction)
    const fastVO2 = vdot * zone.max * taper;
    const slowVO2 = vdot * zone.min * taper;
    const fastVelocity = velocityFromVO2(fastVO2);
    const slowVelocity = velocityFromVO2(slowVO2);

    const fastMile = (60 / fastVelocity) * METERS_PER_MILE;
    const slowMile = (60 / slowVelocity) * METERS_PER_MILE;
    const fastKm = (60 / fastVelocity) * METERS_PER_KM;
    const slowKm = (60 / slowVelocity) * METERS_PER_KM;

    result[key] = {
      label: zone.label,
      perMile: formatPace(secsPerMile),
      perKm: formatPace(secsPerKm),
      rangeMile: `${formatPace(fastMile)} – ${formatPace(slowMile)}`,
      rangeKm: `${formatPace(fastKm)} – ${formatPace(slowKm)}`,
    };
  }

  return result;
}

/**
 * Main function: given event key and time in seconds, return VDOT + all paces
 * @param {string} eventKey — key from EVENTS object
 * @param {number} timeSeconds
 * @returns {{ vdot: number, paces: object } | null}
 */
export function calculatePaces(eventKey, timeSeconds) {
  // Races created via the multi-event UI store `event` as a JSON-stringified
  // array (e.g. '["1600m"]'). A handful of older code paths copy that value
  // verbatim into benchmarks. Normalize here so legacy/bug rows still resolve.
  let key = eventKey;
  if (typeof key === 'string' && key.startsWith('[')) {
    try {
      const parsed = JSON.parse(key);
      if (Array.isArray(parsed) && parsed.length > 0) key = parsed[0];
    } catch {}
  }
  const event = EVENTS[key];
  if (!event || !timeSeconds || timeSeconds <= 0) return null;

  const vdot = calculateVDOT(event.meters, timeSeconds);
  if (!vdot || vdot <= 0 || !isFinite(vdot)) return null;

  const paces = pacesFromVDOT(vdot);
  return { vdot: Math.round(vdot * 10) / 10, paces };
}

/**
 * Given a list of benchmarks (most-recent first) and an optional pinned id,
 * return the benchmark whose event yields valid VDOT paces.
 *
 * Prefers the pinned row when set and resolvable (mirrors the coach
 * AthleteProfile override behavior); otherwise walks the list and returns
 * the first row that calculatePaces() accepts — so a junk most-recent
 * benchmark (custom "Other" event, malformed event field, etc.) silently
 * falls back to the next valid one instead of blanking the UI.
 *
 * @param {Array<{id:string, event:string, time_seconds:number}>} benchmarks
 * @param {string|null} pinnedBenchmarkId
 * @returns {object|null}
 */
export function pickPaceBenchmark(benchmarks, pinnedBenchmarkId) {
  if (!Array.isArray(benchmarks) || benchmarks.length === 0) return null;
  if (pinnedBenchmarkId) {
    const pinned = benchmarks.find(b => b.id === pinnedBenchmarkId);
    if (pinned && calculatePaces(pinned.event, pinned.time_seconds)) return pinned;
  }
  for (const b of benchmarks) {
    if (calculatePaces(b.event, b.time_seconds)) return b;
  }
  return null;
}

/**
 * Parse a time string into total seconds.
 * Accepts: SS.ss | M:SS.ss | H:MM:SS.ss
 * @param {string} timeStr
 * @returns {number} seconds (may be fractional)
 */
export function parseTimeToSeconds(timeStr) {
  const str = timeStr.trim();
  if (!str) return 0;
  const parts = str.split(':').map(Number);
  if (parts.some(isNaN)) return 0;
  if (parts.length === 1) return parts[0];                                      // SS.ss
  if (parts.length === 2) return parts[0] * 60 + parts[1];                     // M:SS.ss
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];  // H:MM:SS.ss
  return 0;
}

/**
 * Format seconds into a readable time string.
 * Fractional seconds are shown when present (e.g. "4:32.56", "1:02:34.56").
 * @param {number} totalSeconds
 * @returns {string}
 */
export function formatTime(totalSeconds) {
  const hasFrac = totalSeconds % 1 !== 0;
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const sFull = totalSeconds % 60;
  const sInt = Math.floor(sFull);
  const sFrac = hasFrac ? '.' + (sFull - sInt).toFixed(2).slice(2) : '';
  const sStr = sInt.toString().padStart(2, '0') + sFrac;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${sStr}`;
  return `${m}:${sStr}`;
}

export const ZONE_ORDER = [
  'recovery', 'easy', 'moderate', 'sub_tempo', 'tempo', 'lt2', 'lt1',
  'aerobic_power', 'vo2max', 'mixed_zone',
  'anaerobic_capacity', 'anaerobic_power', 'speed_endurance', 'speed',
];

/**
 * Apply altitude-based pace adjustment per Jack Daniels' guidelines.
 *
 * Below 3,000 ft: no adjustment.
 * Aerobic zones (recovery → lt2):      +4.5 sec/mile per 1,000 ft above 3,000 ft.
 * Mixed zones (lt1 → mixed_zone):      +2.25 sec/mile per 1,000 ft.
 * Anaerobic zones (capacity → speed):  no adjustment (short, oxygen-independent).
 *
 * @param {object} paces      - output of pacesFromVDOT()
 * @param {number|null} altitudeFt - training altitude in feet
 * @returns {object} altitude-adjusted paces (same shape)
 */
export function applyAltitudeAdjustment(paces, altitudeFt) {
  if (!altitudeFt || altitudeFt < 3000) return paces;
  const thousandFtAbove = (altitudeFt - 3000) / 1000;
  const AEROBIC = new Set(['recovery', 'easy', 'moderate', 'sub_tempo', 'tempo', 'lt2']);
  const MIXED   = new Set(['lt1', 'aerobic_power', 'vo2max', 'mixed_zone']);
  const METERS_PER_MILE = 1609.34;

  function parsePaceStr(str) {
    const [m, s] = str.split(':').map(Number);
    return m * 60 + s;
  }
  function addToPace(str, addMi) {
    return formatPace(parsePaceStr(str) + addMi);
  }
  function addToRange(str, addMi) {
    const [a, b] = str.split(' – ');
    return `${addToPace(a, addMi)} – ${addToPace(b, addMi)}`;
  }

  const result = {};
  for (const [key, zone] of Object.entries(paces)) {
    let addMi = 0;
    if (AEROBIC.has(key)) addMi = thousandFtAbove * 4.5;
    else if (MIXED.has(key)) addMi = thousandFtAbove * 2.25;
    if (addMi === 0) { result[key] = zone; continue; }
    const addKm = addMi * (1000 / METERS_PER_MILE);
    result[key] = {
      ...zone,
      perMile: addToPace(zone.perMile, addMi),
      perKm: addToPace(zone.perKm, addKm),
      rangeMile: addToRange(zone.rangeMile, addMi),
      rangeKm: addToRange(zone.rangeKm, addKm),
    };
  }
  return result;
}
